import { Collection, getFirebase } from 'firestorter';
import CollectionName from '../../utils/collections';
import BaseDocument from '../../Model/BaseDocument';
import Product from '../../Model/Product';

// Shared so an unset field yields one array identity: `|| []` mints a new one on every read,
// and an effect keyed on `mealTimes` then re-runs and re-renders without end.
const NO_MEAL_TIMES = [];

const RefreshInterval = {
  WEEKLY: 'WEEKLY',
  BIWEEKLY: 'BIWEEKLY',
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QUARTERLY',
  NEVER: 'NEVER',
};

/**
 * A plan is replaced by one for the same product. An unscoped plan predates the backfill and has
 * no product to match on, so the coach scopes it instead — without that, one coach's assignment
 * retires the other coach's pre-backfill plan. Missing either coach stays permissive, which is
 * the single-coach behaviour this had before.
 * Must stay identical to `isSupersededByProduct` in `functions-22/src/utils/meal-plan.ts`.
 */
const isSupersededByProduct = (existingData, productId, coachId) => {
  if (existingData.productId) {
    return existingData.productId === productId;
  }
  return !coachId || !existingData.coach || existingData.coach === coachId;
};

class MealPlanAssignment extends BaseDocument {
  constructor(id, opts) {
    super(`${CollectionName.MEAL_PLAN_ASSIGNMENT}/${id}`, opts);
  }

  get coach() {
    return this.data.coach;
  }

  get user() {
    return this.data.user;
  }

  get mealPlanId() {
    return this.data.mealPlanId;
  }

  get lastUpdated() {
    return this.data.lastUpdated;
  }

  get restrictions() {
    return this.data.restrictions || [];
  }

  get approvalDate() {
    return this.data.approvalDate;
  }

  get customMealPlanIntroMessage() {
    return this.data.customMealPlanIntroMessage || '';
  }

  get current() {
    return this.data.current;
  }

  get name() {
    return this.data.name || '';
  }

  get macroAverages() {
    return this.data.macroAverages;
  }

  get mealTimes() {
    return this.data.mealTimes || NO_MEAL_TIMES;
  }

  get allergenTags() {
    return this.data.allergenTags || [];
  }

  get recipes() {
    return this.data.recipes || [];
  }

  get type() {
    return this.data.type || '';
  }

  // Empty on assignments written before the backfill.
  get productId() {
    return this.data.productId || '';
  }

  // Denormalised for display. Not kept in step with a product rename.
  get productName() {
    return this.data.productName || '';
  }

  async setCustomMealPlanIntroMessage(message) {
    return this.updateFields({
      customMealPlanIntroMessage: message || '',
    });
  }

  // Creates a new assignment and atomically deactivates ALL existing current assignments
  async updateMealPlan(newMealPlanData, { approvedBy } = {}) {
    const currentData = this.data;
    const date = new Date();
    const db = getFirebase().default.firestore();
    const batch = db.batch();

    const existingSnapshot = await db
      .collection(CollectionName.MEAL_PLAN_ASSIGNMENT)
      .where('user', '==', currentData.user)
      .where('current', '==', true)
      .get();

    existingSnapshot.docs.forEach((doc) => {
      if (isSupersededByProduct(doc.data(), currentData.productId, currentData.coach)) {
        batch.update(doc.ref, { current: false, lastUpdated: date });
      }
    });

    const {
      mealTimes,
      allergenTags,
      recipes,
      macroAverages,
      name,
      mealPlanId,
      type,
    } = newMealPlanData;

    // Create new assignment in the same batch
    const newRef = db.collection(CollectionName.MEAL_PLAN_ASSIGNMENT).doc();
    batch.set(newRef, {
      ...currentData,
      mealTimes,
      allergenTags,
      recipes,
      macroAverages,
      name,
      mealPlanId,
      current: true,
      lastUpdated: date,
      approvalDate: date,
      type,
      ...(approvedBy && { approvedBy }),
    });

    await batch.commit();
    return newRef.id;
  }

  /**
   * An explicit product wins; otherwise it is inferred only when the client holds exactly one —
   * an unscoped plan is recoverable where a wrongly scoped one is not.
   */
  static async resolveAssignmentProduct(userDoc, productId) {
    const activeProductIds = userDoc?.activeProductIds || [];
    const resolvedId = productId || (activeProductIds.length === 1 ? activeProductIds[0] : undefined);
    if (!resolvedId) {
      return {};
    }

    const productName = (await Product.getById(resolvedId))?.name;
    return productName ? { productId: resolvedId, productName } : { productId: resolvedId };
  }

  static async addNewAssignment({
    user,
    mealPlanData,
    coach,
    approvedBy,
    productId,
    productName,
  }) {
    const {
      mealTimes,
      mealPlanId,
      name,
      allergenTags,
      macroAverages,
      recipes,
      mealPlanRequestId,
      isGeneratedByAI,
      type,
    } = mealPlanData;
    const date = new Date();
    const db = getFirebase().default.firestore();
    const batch = db.batch();

    const existingSnapshot = await db
      .collection(CollectionName.MEAL_PLAN_ASSIGNMENT)
      .where('user', '==', user)
      .where('current', '==', true)
      .get();

    existingSnapshot.docs.forEach((existingDoc) => {
      if (isSupersededByProduct(existingDoc.data(), productId, coach)) {
        batch.update(existingDoc.ref, { current: false, lastUpdated: date });
      }
    });

    // Create new assignment in the same batch
    const newRef = db.collection(CollectionName.MEAL_PLAN_ASSIGNMENT).doc();
    batch.set(newRef, {
      current: true,
      user,
      coach,
      ...(productId && { productId }),
      ...(productName && { productName }),
      mealPlanId,
      mealTimes,
      name,
      allergenTags,
      macroAverages,
      recipes,
      approvalDate: date,
      lastUpdated: date,
      type,
      ...(mealPlanRequestId && { mealPlanRequestId }),
      ...(isGeneratedByAI && { isGeneratedByAI }),
      ...(approvedBy && { approvedBy }),
    });

    await batch.commit();

    const doc = new MealPlanAssignment(newRef.id);
    await doc.init();
    return doc;
  }

  /**
   * Every current assignment for a product the client still holds, newest first. A cancelled
   * product's plan stays `current: true` forever, so `current` alone is not enough. Without
   * `userDoc` the filter is skipped. Scoped to one coach when given one, so a client with two
   * relationships does not show one coach the other's plans.
   */
  static async getUserMealPlanAssignments(user, userDoc, coachId) {
    const collection = new Collection(CollectionName.MEAL_PLAN_ASSIGNMENT, {
      createDocument: ({ id }, opts) => new MealPlanAssignment(id, opts),
      query: (ref) => {
        const query = ref
          .where('user', '==', user)
          .where('current', '==', true);
        return coachId ? query.where('coach', '==', coachId) : query;
      },
    });
    await collection.fetch();

    if (!collection.hasDocs) {
      return [];
    }

    // Newest first, so a caller that wants "the" plan gets the most recent one.
    const sorted = [...collection.docs].sort((a, b) => (
      (b.approvalDate?.toDate?.()?.getTime() || 0) - (a.approvalDate?.toDate?.()?.getTime() || 0)
    ));

    if (!userDoc) {
      return sorted;
    }

    const activeProductIds = new Set(userDoc.activeProductIds || []);
    return sorted.filter((doc) => !doc.productId || activeProductIds.has(doc.productId));
  }

  static async getMealPlanAssignmentsByCoach(coach, currentPlans = true) {
    const collection = new Collection(CollectionName.MEAL_PLAN_ASSIGNMENT, {
      createDocument: ({ id }, opts) => new MealPlanAssignment(id, opts),
      query: (ref) => {
        const query = ref.where('coach', '==', coach);
        if (currentPlans) {
          return query.where('current', '==', true);
        }
        return query;
      },
    });
    await collection.fetch();
    return collection;
  }

  static async getAllMealPlanAssignments() {
    const collection = new Collection(CollectionName.MEAL_PLAN_ASSIGNMENT, {
      createDocument: ({ id }, opts) => new MealPlanAssignment(id, opts),
    });
    await collection.fetch();
    return collection;
  }

  /**
   * Every assignment the client has had, from every coach. Deliberately not scoped: its one
   * caller excludes recently served recipes, and not repeating a meal is a property of the
   * person rather than of a relationship.
   */
  static async getAssignmentsByUser(userId) {
    const collection = new Collection(CollectionName.MEAL_PLAN_ASSIGNMENT, {
      createDocument: ({ id }, opts) => new MealPlanAssignment(id, opts),
      query: (ref) => ref.where('user', '==', userId),
    });
    await collection.fetch();
    return collection.hasDocs ? collection.docs : [];
  }
}

export default MealPlanAssignment;
export { RefreshInterval };
